import { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { ContactForm } from '@/components/ContactForm';
import { ScrollProgress, ScrollToTop } from '@/components/ScrollAnimations';
import AnimatedPageHero from '@/components/AnimatedPageHero';
import { Helmet } from 'react-helmet-async';
import { siteConfig } from '@/data/siteData';
import { 
  Target, 
  Eye, 
  Users, 
  CheckCircle2,
  Lightbulb,
  Shield,
  Award,
  Handshake
} from 'lucide-react';

const values = [
  { icon: Target, title: 'Customer Focus', description: 'Delivering tailored solutions that address unique business challenges and drive measurable outcomes.' },
  { icon: Lightbulb, title: 'Innovation', description: 'Continuously adopting cutting-edge technologies to keep our clients ahead of the competition.' },
  { icon: Shield, title: 'Integrity', description: 'Maintaining the highest standards of transparency, ethics, and professional conduct.' },
  { icon: Handshake, title: 'Partnership', description: 'Building long-term relationships through trust, reliability, and consistent value delivery.' },
];

const milestones = [
  { year: '2002', event: 'Company founded in Chennai with a vision to deliver enterprise IT solutions' },
  { year: '2008', event: 'Strategic expansion to Cochin and Bangalore to serve South India' },
  { year: '2012', event: 'Achieved ISO 9001 and ISO 27001 certifications' },
  { year: '2016', event: 'Launched dedicated Cyber Security division for enterprise clients' },
  { year: '2018', event: 'Expanded to Mumbai and Hyderabad offices' },
  { year: '2022', event: 'International expansion with Dubai and Singapore offices' },
  { year: '2024', event: 'Serving 100+ enterprise clients with 100+ certified professionals' },
];

export const AboutPage = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>About Us - Felix Infotech | 22+ Years of Technology Excellence</title>
        <meta name="description" content="Felix Infotech is a leading IT solutions provider with 22+ years of experience. ISO 9001 & ISO 27001 certified. Serving 100+ enterprise clients across India, Dubai & Singapore." />
        <meta name="keywords" content="Felix Infotech about, IT company Chennai, system integrator India, ISO certified IT company, enterprise IT solutions provider, managed services India" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://felixinfotech.com/about" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="About Felix Infotech - 22+ Years of Technology Excellence" />
        <meta property="og:description" content="Leading IT solutions provider. ISO 9001 & ISO 27001 certified. 100+ enterprise clients across India, Dubai & Singapore." />
        <meta property="og:url" content="https://felixinfotech.com/about" />
        <meta property="og:site_name" content="Felix Infotech" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="About Felix Infotech | 22+ Years of Excellence" />
        <meta name="twitter:description" content="ISO certified IT solutions provider with 22+ years of experience, serving 100+ enterprise clients." />
      </Helmet>

      <ScrollProgress />
      <Navbar />

      <main>
        {/* Hero Section */}
        <AnimatedPageHero
          title="Pioneering Technology Excellence"
          subtitle="About Felix Infotech"
          description="Transforming enterprises with innovative IT solutions for over 22 years"
          badge="About Us"
          animationType="about"
          gradient="from-[#0066CC] to-[#004499]"
          breadcrumbs={[{ label: 'About Us' }]}
        />

        {/* About Content */}
        <section className="py-20 bg-white" ref={ref}>
          <div className="container mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Image */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                className="relative"
              >
                <div className="relative rounded-3xl overflow-hidden shadow-xl">
                  <img
                    src="https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5?w=800&q=80"
                    alt="Felix Infotech Team Collaboration"
                    className="w-full h-auto"
                  />
                </div>
                
                {/* Stats Card */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ delay: 0.3 }}
                  className="absolute -bottom-8 left-8 bg-white rounded-2xl p-6 shadow-xl border border-gray-100"
                >
                  <div className="text-center">
                    <p className="text-4xl font-bold text-[#0066CC]">{siteConfig.experience}</p>
                    <p className="text-sm text-gray-500">Years of Excellence</p>
                  </div>
                </motion.div>
              </motion.div>

              {/* Content */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                className="space-y-6"
              >
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
                  Your Trusted Partner for Innovative IT Solutions
                </h2>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Felix Infotech Pvt Ltd is a premier system integrator and managed services provider 
                  with 22+ years of IT transformation excellence. We enable secure, scalable, and intelligent 
                  IT ecosystems for enterprises across India, Dubai, and Singapore.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Headquartered in Chennai with offices in 8 strategic locations including Mumbai, Bangalore, 
                  Hyderabad, Cochin, Coimbatore, Dubai, and Singapore. Our ISO 9001 and ISO 27001 certified 
                  operations reflect our commitment to quality and security. As authorized partners for 40+ 
                  global technology vendors, we bring world-class solutions to businesses of all sizes.
                </p>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4">
                  {[
                    { value: siteConfig.experience, label: 'Years' },
                    { value: siteConfig.clients, label: 'Clients' },
                    { value: siteConfig.projects, label: 'Projects' },
                    { value: siteConfig.employees, label: 'Experts' },
                  ].map((stat, index) => (
                    <div key={index} className="text-center p-4 bg-gray-50 rounded-xl">
                      <p className="text-2xl font-bold text-[#0066CC]">{stat.value}</p>
                      <p className="text-xs text-gray-500">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Mission & Vision - Fixed icons */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Mission */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white rounded-3xl p-8 lg:p-10 shadow-lg border border-gray-100"
              >
                <div className="w-16 h-16 rounded-2xl bg-[#0066CC] flex items-center justify-center mb-6">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
                <p className="text-gray-600 leading-relaxed">
                  To empower businesses with innovative, reliable, and cost-effective technology solutions 
                  that drive operational excellence and sustainable growth. We are committed to being the 
                  trusted technology partner for organizations seeking to thrive in the digital economy, 
                  delivering measurable value through expertise, integrity, and customer-centric service.
                </p>
              </motion.div>

              {/* Vision */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-3xl p-8 lg:p-10 shadow-lg border border-gray-100"
              >
                <div className="w-16 h-16 rounded-2xl bg-[#0066CC] flex items-center justify-center mb-6">
                  <Eye className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
                <p className="text-gray-600 leading-relaxed">
                  To be the leading technology solutions provider in Asia, recognized for our innovation, 
                  technical excellence, and unwavering commitment to customer success. We envision a future 
                  where every enterprise, regardless of size, has access to world-class technology infrastructure 
                  that enables them to compete on a global scale.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <p className="text-xs font-semibold tracking-widest uppercase text-[#0066CC] mb-3">Our Values</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
                The Principles That Guide Us
              </h2>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center p-6 rounded-2xl bg-gray-50 hover:shadow-lg transition-shadow"
                >
                  <div className="w-14 h-14 rounded-xl bg-[#0066CC]/10 flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-7 h-7 text-[#0066CC]" />
                  </div>
                  <h3 className="font-semibold text-lg text-gray-900 mb-2">{value.title}</h3>
                  <p className="text-sm text-gray-600">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <p className="text-xs font-semibold tracking-widest uppercase text-[#0066CC] mb-3">Our Journey</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
                Key Milestones
              </h2>
            </motion.div>

            <div className="max-w-3xl mx-auto">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="flex items-center gap-6 mb-8"
                >
                  <div className="w-20 text-right">
                    <span className="text-xl font-bold text-[#0066CC]">{milestone.year}</span>
                  </div>
                  <div className="w-4 h-4 rounded-full bg-[#0066CC] flex-shrink-0" />
                  <div className="flex-1 bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <p className="text-gray-700">{milestone.event}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Certifications */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <p className="text-xs font-semibold tracking-widest uppercase text-[#0066CC] mb-3">Quality Assurance</p>
              <h2 className="text-3xl font-bold text-gray-900">
                Industry Certifications
              </h2>
            </motion.div>
            <div className="flex justify-center items-center gap-12 flex-wrap">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-gray-50 rounded-2xl p-8 shadow-sm border border-gray-100"
              >
                <img src="https://felixinfotech.com/img/iso-1.png" alt="ISO 9001:2015" className="h-28 w-auto" style={{ mixBlendMode: 'multiply' }} />
                <p className="text-center mt-4 font-medium text-gray-900">ISO 9001:2015</p>
                <p className="text-center text-sm text-gray-500">Quality Management</p>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-gray-50 rounded-2xl p-8 shadow-sm border border-gray-100"
              >
                <img src="https://felixinfotech.com/img/iso-2.png" alt="ISO 27001:2013" className="h-28 w-auto" style={{ mixBlendMode: 'multiply' }} />
                <p className="text-center mt-4 font-medium text-gray-900">ISO 27001:2013</p>
                <p className="text-center text-sm text-gray-500">Information Security</p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <ContactForm />
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
};

export default AboutPage;
